import { Box } from '@mui/material';
import React from 'react';
import UWInboxAccNotes from '../../../../_assets/svg/UWInboxAccNotes.svg';
import { type SubmissionTableRow } from '../../../../api/submissions/submissions.api.types';
import SimpleTooltip from '../../../../components/SimpleTooltip';
import { useFetchSubmissionStatusOptions } from '../../../../components/hooks/queries/submissions/useFetchSubmissionStatusOptions';
import { useAuth } from '../../../../components/hooks/useAuth';
import { useIsZurichCarrier } from '../../../../components/hooks/useIsZurichCarrier';
import useGetProductLines from '../../../../components/hooks/useGetProductLines';
import { Note } from '../../../../components/notes/notes.helper';
import AccountLink from '../../../../components/tables/cell-components/account-link/AccountLink';
import { getAccountGroupTypeFilterColumn } from '../../../../components/tables/tableFilters/accountGroupTypeFilter';
import { REDACTED_FIELD } from '../../../../config/logrocket';
import { useCowbellTranslations } from '../../../../i18n/translations';
import { TwoLineTextWithTooltip } from '../../../../components/TwoLineTextWithTooltip';
import { getIsUsPlatform, getIsAuPlatform } from '../../../../utils';
import { YesNoOptions, getBoolValueText } from '../../../../utils/appUtils';
import { usePlatformLanguage } from '../../../../utils/hooks/usePlatformLanguage';
import { PERSONA_TYPES } from '../../../_statics/persona.statics';
import { CowbellSubmissionsWorkspaceOptions } from '../CowbellSubmissionsWorkspace/CowbellSubmissionsWorkspaceOptions';
import { AgencySubmissionsOptions } from '../CowbellSubmissionsWorkspace/AgencySubmissionsOptions';
import {
  getAccountGroupColumns,
  getAgentCellValue,
  getFlagsColumn,
  SubmissionLinkCell,
  SubmissionStatusCell,
} from '../CowbellSubmissionsWorkspace/helpers';
import { getLocalDateString } from '../../../../utils/csvUtils';
import { relabelStatusOptions } from '../../utils/submissionStatusLabel';
import { stageLabel } from '../../utils/stageLabel';
import { formatTableCurrencyByRegion } from '../../../../utils/currency.utils';

export enum SUBMISSION_SOURCE {
  API = 'API',
  BROKER_PORTAL = 'BROKER_PORTAL',
  EMAIL = 'EMAIL',
  OMNI = 'OMNI',
}

export const SUBMISSION_SOURCE_LIST = [
  SUBMISSION_SOURCE.API,
  SUBMISSION_SOURCE.BROKER_PORTAL,
  SUBMISSION_SOURCE.EMAIL,
  SUBMISSION_SOURCE.OMNI,
];

interface ColumnConfigOptions {
  /** Adds the Stage column. Only the Cowbell Portal and All tabs ask for it. */
  readonly showStage?: boolean;
}

export const useColumnConfig = ({
  showStage = false,
}: ColumnConfigOptions = {}) => {
  const submissionsStatusesOptionsQuery = useFetchSubmissionStatusOptions();
  const productLinesQuery = useGetProductLines();
  const { t } = useCowbellTranslations();
  const auth = useAuth();
  const isZurichCarrier = useIsZurichCarrier();
  const region = usePlatformLanguage();

  return React.useMemo(
    () => ({
      id: {
        name: 'id',
        title: 'ID',
        filterType: 'string',
        align: 'left',
        show: false,
        sort: false,
      },
      flags: getFlagsColumn(),
      ...getAccountGroupColumns(),
      accountGroupType: getAccountGroupTypeFilterColumn(),
      submissionNumber: {
        name: 'submissionNumber',
        title: 'Submission Number',
        filterType: 'string',
        fixed: 'left',
        width: 170,
        canToggle: false,
        getCellValue: (row: SubmissionTableRow) => {
          return (
            <SubmissionLinkCell
              submissionId={row.id}
              text={row.submissionNumber}
            />
          );
        },
        sort: false,
      },
      'firmographics.name': {
        name: 'firmographics.name',
        title: 'Account',
        filterType: 'string',
        canToggle: false,
        className: `${REDACTED_FIELD}`,
        width: 'l',
        getCellValue: (row: SubmissionTableRow) => (
          <AccountLink accountId={row.agencyAccount?.accountId}>
            <TwoLineTextWithTooltip
              text={String(row.firmographics.name || '')}
              variant="body2"
              sx={{ fontSize: '1rem' }}
            />
          </AccountLink>
        ),
        sort: false,
      },
      productLines: {
        name: 'productLines',
        title: 'LOB',
        filterType: 'list',
        getListOptions: () => productLinesQuery.data ?? [],
        width: 90,
        getCellValue: (row: SubmissionTableRow) => {
          const productLines = row.productLines
            ?.map((productLine) => productLine.name)
            .join(', ');

          return (
            <TwoLineTextWithTooltip
              text={productLines || row.productLine?.name || '-'}
              variant="body2"
              sx={{ fontSize: '1rem' }}
            />
          );
        },
        sort: false,
      },
      isExcess: {
        name: 'isExcess',
        title: 'Excess',
        filterType: 'bool',
        width: 90,
        sort: false,
        getListOptions: () => YesNoOptions,
        getCellValue: (row: SubmissionTableRow) =>
          getBoolValueText(row.isExcess),
        csv: (row: SubmissionTableRow) => getBoolValueText(row.isExcess),
      },
      ...(isZurichCarrier && {
        crimeLimit: {
          name: 'crimeLimit',
          title: 'Crime Limit',
          align: 'right',
          sort: false,
          getCellValue: formatTableCurrencyByRegion()('crimeLimit'),
          csv: formatTableCurrencyByRegion()('crimeLimit'),
        },
      }),
      agent: {
        name: 'agent',
        title: `${t('agent')}`,
        show: false,
        className: `${REDACTED_FIELD}`,
        getCellValue: getAgentCellValue,
        csv: getAgentCellValue,
        sort: false,
      },
      'agencyAccount.agent.emailAddress': {
        name: 'agencyAccount.agent.emailAddress',
        title: `${t('agent')} Email`,
        filterType: 'string',
        className: `${REDACTED_FIELD}`,
        getCellValue: (row: SubmissionTableRow) =>
          row.agencyAccount?.agent?.emailAddress ?? '-',
        sort: false,
      },
      ...(getIsUsPlatform(region) || getIsAuPlatform(region)
        ? {
            'firmographics.physicalAddress.state': {
              name: 'firmographics.physicalAddress.state',
              title: 'State',
              width: 80,
              className: `${REDACTED_FIELD}`,
              getCellValue: (row: SubmissionTableRow) => (
                <span style={{ textTransform: 'uppercase' }}>
                  {row.firmographics?.physicalAddress?.state ?? '-'}
                </span>
              ),
              sort: false,
            },
          }
        : {}),
      ...(auth.accountType === PERSONA_TYPES.AGENCY
        ? {}
        : {
            'agencyAccount.agencyName': {
              name: 'agencyAccount.agencyName',
              title: t('agency'),
              width: 'l',
              className: `${REDACTED_FIELD}`,
              getCellValue: (row: SubmissionTableRow) => (
                <TwoLineTextWithTooltip
                  text={row.agencyAccount?.agencyName ?? '-'}
                  variant="body2"
                  sx={{ fontSize: '1rem' }}
                />
              ),
              sort: false,
            },
          }),
      created: {
        name: 'created',
        title: 'Created Date',
        filterType: 'date',
        getCellValue: (row: SubmissionTableRow) =>
          getLocalDateString(row.created),
      },
      modified: {
        name: 'modified',
        title: 'Modified Date',
        filterType: 'date',
        getCellValue: (row: SubmissionTableRow) =>
          getLocalDateString(row.modified),
      },
      ...(auth.accountType === PERSONA_TYPES.AGENCY
        ? {}
        : {
            'agencyAccount.id.agencyId': {
              name: 'agencyAccount.id.agencyId',
              title: 'Agency ID',
              filterType: 'string',
              show: false,
              sort: false,
              width: 'l',
              getCellValue: (row: SubmissionTableRow) =>
                row.agencyAccount?.agencyId,
            },
          }),
      'agencyAccount.id.accountId': {
        name: 'agencyAccount.id.accountId',
        title: 'Account ID',
        filterType: 'string',
        show: false,
        sort: false,
        width: 'l',
        getCellValue: (row: SubmissionTableRow) => row.agencyAccount?.accountId,
      },
      submissionSource: {
        name: 'submissionSource',
        title: 'Submission Source',
        filterType: 'list',
        show: true,
        sort: false,
        getListOptions: () =>
          SUBMISSION_SOURCE_LIST.map((_option) => ({
            label: _option,
            value: _option,
          })),
        getCellValue: (row: SubmissionTableRow) => row.submissionSource,
      },
      submissionStatus: {
        name: 'submissionStatus',
        title: 'Submission Status',
        filterType: 'list',
        getListOptions: () =>
          relabelStatusOptions(submissionsStatusesOptionsQuery.data),
        getCellValue: (row: SubmissionTableRow) => (
          <SubmissionStatusCell
            submissionStatus={row.submissionStatus}
            declinationReasons={row.declineReasons?.allDeclineReasons ?? []}
            carrierReferralReasons={row.carrierReferralReasons ?? []}
          />
        ),
        sort: false,
      },
      ...(showStage
        ? {
            currentStage: {
              name: 'currentStage',
              title: 'Stage',
              sort: false,
              getCellValue: (row: SubmissionTableRow) =>
                stageLabel(row.currentStage, row.currentStageState),
            },
          }
        : {}),
      notes: {
        name: 'notes',
        title: 'Notes',
        width: 180,
        align: 'center',
        ommitInCsv: true,
        disableExpandRow: true,
        getCellValue: (row: SubmissionTableRow) => {
          const attachmentMetaData = {
            accountId: row.agencyAccount?.accountId,
            agencyId: row.agencyAccount?.agencyId,
            companyName: row.firmographics.name,
          };

          return (
            <Box display="flex" justifyContent="center" alignItems="center">
              <SimpleTooltip title="Submission Notes">
                <UWInboxAccNotes
                  onClick={(event: any) => {
                    event.stopPropagation();
                    Note.show('submission', {
                      data: {
                        submissionNumber: row.submissionNumber,
                        attachmentMetaData,
                      },
                    });
                  }}
                />
              </SimpleTooltip>
            </Box>
          );
        },
        sort: false,
      },
      actions: {
        name: 'actions',
        width: 'xxs',
        fixed: 'right',
        align: 'center',
        sort: false,
        ommitInCsv: true,
        getCellValue: (row: SubmissionTableRow) => {
          if (auth.accountType === PERSONA_TYPES.COWBELL) {
            return <CowbellSubmissionsWorkspaceOptions data={row} />;
          }
          return <AgencySubmissionsOptions data={row} />;
        },
      },
    }),
    [
      productLinesQuery.data,
      submissionsStatusesOptionsQuery.data,
      t,
      region,
      auth.accountType,
      isZurichCarrier,
      showStage,
    ]
  );
};
